import { lazy, type ComponentType } from "react";

type RouteEntry = {
  pattern: string | RegExp;
  load: () => Promise<{ default: ComponentType<any> }>;
  component: React.LazyExoticComponent<ComponentType<any>>;
};

function resolved<T>(value: T): Promise<T> {
  const p = Promise.resolve(value);
  (p as any).status = "fulfilled";
  (p as any).value = value;
  return p;
}

function entry(pattern: string | RegExp, loadFn: () => Promise<{ default: ComponentType<any> }>): RouteEntry {
  let mod: { default: ComponentType<any> } | null = null;
  const load = () => loadFn().then(m => { mod = m; return m; });
  const component = lazy(() => mod ? resolved(mod) : load());
  return { pattern, load, component };
}

function namedEntry(pattern: string, importFn: () => Promise<any>, exportName: string): RouteEntry {
  let mod: { default: ComponentType<any> } | null = null;
  const load = () => importFn().then((m: any) => { const r = { default: m[exportName] }; mod = r; return r; });
  const component = lazy(() => mod ? resolved(mod) : load());
  return { pattern, load, component };
}

const videotapes = entry("/services/videotapes", () => import("@/pages/Videotapes"));
const vhsToDigital = entry("/services/vhs-to-digital", () => import("@/pages/VHSToDigital"));
const formatVHS = entry("/formats/vhs", () => import("@/pages/FormatVHS"));
const formatVideo8 = entry("/formats/video8", () => import("@/pages/FormatVideo8"));
const formats = entry(/^\/formats(\/|$)/, () => import("@/pages/FormatCategory"));
const setup = entry("/setup", () => import("@/pages/SetupFlow"));
// Preloaded before hydration: a lazy chunk still loading at hydration time lets
// React swap the server HTML for the route skeleton (content flash, late LCP, CLS).
const theVault = entry("/the-vault", () => import("@/pages/TheVault"));
const confirmation = entry(/^\/confirmation\//, () => import("@/pages/Confirmation"));
const blog = entry("/blog", () => import("@/pages/Blog"));
const blogCategory = entry(/^\/blog\/category\//, () => import("@/pages/BlogCategoryPage"));
const blogPost = entry(/^\/blog\//, () => import("@/pages/BlogPost"));
const local = entry("/local", () => import("@/pages/LocalHub"));
const cityPage = entry(/^\/local\//, () => import("@/pages/CityPage"));
const customerStory = entry(/^\/reviews\//, () => import("@/pages/CustomerStory"));
const reviews = entry("/reviews", () => import("@/pages/Reviews"));
const legacySelfStorage = entry("/legacy-self-storage", () => import("@/pages/LegacySelfStorage"));
const whyCapture = entry("/why-capture", () => import("@/pages/WhyCapture"));
const captureComparison = entry("/capture-comparison", () => import("@/pages/CaptureComparison"));
const captureFacts = entry("/about/facts", () => import("@/pages/CaptureFacts"));
const contact = entry("/contact", () => import("@/pages/Contact"));
const style = entry("/style", () => import("@/pages/Style"));
const admin = entry("/admin", () => import("@/pages/Admin"));

const betamaxToDigital = entry("/services/betamax-to-digital", () => import("@/pages/BetamaxToDigital"));
const vhscToDigital = entry("/services/vhs-c-to-digital", () => import("@/pages/VHSCToDigital"));
const minidvToDigital = entry("/services/minidv-to-digital", () => import("@/pages/MiniDVToDigital"));
const hi8ToDigital = entry("/services/hi8-to-digital", () => import("@/pages/Hi8ToDigital"));
const svcImport = () => import("@/pages/service-pages");
const formatVHSC = namedEntry("/formats/vhs-c", svcImport, "FormatVHSC");
const formatBetamax = namedEntry("/formats/betamax", svcImport, "FormatBetamax");
const formatDV = namedEntry("/formats/dv", svcImport, "FormatDV");
const formatMiniDV = namedEntry("/formats/minidv", svcImport, "FormatMiniDV");
const dvcToDigital = namedEntry("/services/dvc-to-digital", svcImport, "DVCToDigital");
const video8ToDigital = namedEntry("/services/video8-to-digital", svcImport, "Video8ToDigital");
const video8ToDigitalAlias = namedEntry("/services/video-8-to-digital", svcImport, "Video8ToDigital");
const filmHub = namedEntry("/services/film", svcImport, "FilmServicesHub");
const filmTransfer = namedEntry("/services/film-transfer", svcImport, "FilmServicesHub");
const super8ToDigital = namedEntry("/services/super8-to-digital", svcImport, "Super8ToDigital");
const convertSuper8ToDigital = namedEntry("/services/convert-super-8-to-digital", svcImport, "Super8ToDigital");
const film8mmToDigital = namedEntry("/services/8mm-to-digital", svcImport, "Film8mmToDigital");
const film8mmToDigitalAlias = namedEntry("/services/8mm-film-to-digital", svcImport, "Film8mmToDigital");
const film16mmToDigital = namedEntry("/services/16mm-to-digital", svcImport, "Film16mmToDigital");
const photosHub = entry("/services/photos", () => import("@/pages/PhotosHub"));
const photoScanning = entry("/services/photo-scanning", () => import("@/pages/PhotoScanning"));
const photoDigitization = entry("/services/photo-digitization", () => import("@/pages/PhotoScanning"));
const albumScanning = entry("/services/album-scanning", () => import("@/pages/AlbumScanning"));
const photoAlbumScanningService = entry("/services/photo-album-scanning-service", () => import("@/pages/AlbumScanning"));
const slidesNegativesHub = namedEntry("/services/slides-negatives", svcImport, "SlidesNegativesHub");
const slideScanning = namedEntry("/services/slide-scanning", svcImport, "SlideScanning");
const convertSlidesToDigital = namedEntry("/services/convert-slides-to-digital", svcImport, "SlideScanning");
const negativeScanning = namedEntry("/services/negative-scanning", svcImport, "NegativeScanning");
const convertNegativesToDigitalPhotos = namedEntry("/services/convert-negatives-to-digital-photos", svcImport, "NegativeScanning");
const audioHub = namedEntry("/services/audiotapes", svcImport, "AudioServicesHub");
const cassetteToDigital = namedEntry("/services/cassette-to-digital", svcImport, "CassetteToDigital");
const digitalHub = namedEntry("/services/digital", svcImport, "DigitalServicesHub");
const digitalScanning = namedEntry("/services/digital-scanning", svcImport, "DigitalServicesHub");
const videoConversionService = entry("/services/video-conversion-service", () => import("@/pages/Videotapes"));
const dvdTransferRecyclingProgram = namedEntry("/services/dvd-transfer-recycling-program", svcImport, "DVDTransferRecyclingProgram");
const dvdToDigital = entry("/services/dvd-to-digital", () => import("@/pages/DVDToDigital"));
const cdToDigital = entry("/services/convert-cd-to-digital", () => import("@/pages/CDToDigital"));
const memoryCardsToDigital = entry("/services/memory-cards-to-digital", () => import("@/pages/MemoryCardsToDigital"));
const usbFlashDrivesToDigital = entry("/services/usb-flash-drives-to-digital", () => import("@/pages/USBFlashDrivesToDigital"));
const slides35mmToDigital = entry("/services/35mm-slides-to-digital", () => import("@/pages/Slides35mmToDigital"));
const negatives35mmToDigital = entry("/services/35mm-negatives-to-digital", () => import("@/pages/Negatives35mmToDigital"));
const usbDelivery = entry("/services/usb", () => import("@/pages/USBDelivery"));
const googlePhotos = namedEntry("/google-photos", svcImport, "GooglePhotosPage");
const gifting = namedEntry("/gifting", svcImport, "GiftingPage");
const corporate = namedEntry("/corporate", svcImport, "CorporatePage");
const partners = namedEntry("/partners", svcImport, "PartnersPage");
const faq = namedEntry("/faq", svcImport, "FAQPage");
const howItWorks = namedEntry("/how-it-works", svcImport, "HowItWorksPage");
const about = namedEntry("/about", svcImport, "AboutPage");
const ourStory = entry("/our-story", () => import("@/pages/OurStory"));
const pricing = entry("/pricing", () => import("@/pages/Pricing"));
const developers = entry("/developers", () => import("@/pages/Developers"));
const deals = entry("/deals", () => import("@/pages/Deals"));
const leadership = entry("/about/leadership", () => import("@/pages/Leadership"));
const privacy = namedEntry("/privacy", svcImport, "PrivacyPage");
const terms = namedEntry("/terms", svcImport, "TermsPage");
const shippingPolicy = namedEntry("/shipping-policy", svcImport, "ShippingPolicyPage");
const talkToAlex = entry("/talk-to-alex", () => import("@/pages/TalkToAlex"));
const freeQuote = entry("/free-quote", () => import("@/pages/FreeQuote"));
const memoryPlanner = entry("/memory-planner", () => import("@/pages/MemoryPlanner"));
const giftCardsHome = entry("/gift-cards", () => import("@/pages/GiftCardsHome"));
const giftCards = entry("/gift-cards/purchase", () => import("@/pages/GiftCards"));
const giftCardsBalance = entry("/gift-cards/balance", () => import("@/pages/GiftCardsBalance"));
const costcoSignup = entry("/costco", () => import("@/pages/CostcoSignup"));
const cvsDigitizationServices = entry("/cvs-digitization-services", () => import("@/pages/CvsDigitizationServices"));
const samsClubDigitizationServices = entry("/sams-club-digitization-services", () => import("@/pages/SamsClubDigitizationServices"));
const walmartDigitizationServices = entry("/walmart-digitization-services", () => import("@/pages/WalmartDigitizationServices"));

export const routes = {
  videotapes, vhsToDigital, formatVHS, formatVideo8, formatVHSC, formatBetamax, formatDV, formatMiniDV, formats,
  setup, confirmation, blog, blogCategory, blogPost, local, cityPage, customerStory, reviews,
  legacySelfStorage, whyCapture, captureComparison, contact, style, admin,
  freeQuote, memoryPlanner, giftCardsHome, giftCards, giftCardsBalance, costcoSignup, cvsDigitizationServices, samsClubDigitizationServices, walmartDigitizationServices,
  betamaxToDigital, vhscToDigital, minidvToDigital, hi8ToDigital, dvcToDigital,
  video8ToDigital, video8ToDigitalAlias, filmHub, filmTransfer,
  super8ToDigital, convertSuper8ToDigital,
  film8mmToDigital, film8mmToDigitalAlias, film16mmToDigital,
  photosHub, photoScanning, photoDigitization,
  albumScanning, photoAlbumScanningService,
  slidesNegativesHub, slideScanning, convertSlidesToDigital,
  negativeScanning, convertNegativesToDigitalPhotos,
  audioHub, cassetteToDigital,
  digitalHub, digitalScanning,
  videoConversionService, dvdTransferRecyclingProgram,
  dvdToDigital, cdToDigital, memoryCardsToDigital, usbFlashDrivesToDigital,
  slides35mmToDigital, negatives35mmToDigital, usbDelivery,
  googlePhotos, gifting, corporate, partners,
  faq, howItWorks, about, captureFacts, ourStory, leadership, privacy, terms, shippingPolicy,
  pricing, developers, deals,
  talkToAlex, theVault,
};

const allRoutes = Object.values(routes);

export function preloadRouteForPath(pathname: string): Promise<any> | null {
  const normalized = pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
  for (const route of allRoutes) {
    if (typeof route.pattern === "string") {
      if (normalized === route.pattern) return route.load();
    } else {
      if (route.pattern.test(normalized)) return route.load();
    }
  }
  return null;
}
